/**
 * Which picture a ContactAvatar paints, and what a failed load of it means.
 *
 * Two kinds of URL reach an avatar:
 *  - an OVERRIDE (`profilePicURL`) — the caller's picture beats the cache;
 *  - a FALLBACK (`fallbackPicURL`) — a hint, used only while the cache has no
 *    picture. The planner hands in the URL a message stored for its recipient
 *    at compose time. WhatsApp picture URLs expire, so a hint that fails to load
 *    says nothing about the cache and must not be treated as the cache's
 *    picture having expired.
 */
export interface AvatarPictureInput {
    profilePicURL?: string;
    cachedPic?: string;
    /** The planner's contact-pic map entry (`CalendarReducer.contactPicMap`). */
    mappedPic?: string;
    fallbackPicURL?: string;
    /** The URL an <img> most recently failed to load, if any. */
    failedUrl?: string;
}

export interface AvatarPicture {
    /** What to paint; undefined means the silhouette. */
    src?: string;
    /** `src` is the fallback hint, not the cache's own picture. */
    fromFallback: boolean;
    /** `src` already failed to load — paint the silhouette instead. */
    failed: boolean;
}

export function resolveAvatarPicture(input: AvatarPictureInput): AvatarPicture {
    // Exactly the expression the avatar has always used for its own picture,
    // so every caller that does not pass a fallback is unaffected.
    const primary = input.profilePicURL ?? input.cachedPic ?? input.mappedPic;
    const fallback = !primary && input.fallbackPicURL && input.fallbackPicURL !== input.failedUrl
        ? input.fallbackPicURL
        : undefined;
    const src = fallback ?? primary;
    return { src, fromFallback: fallback !== undefined, failed: !!src && src === input.failedUrl };
}

/**
 * A failed load of the cache's OWN picture means that URL has expired: clear it
 * (in the store and, on the website, in the persisted cache) and allow one
 * re-fetch. A failed FALLBACK means only that the hint is stale — there is
 * nothing in the cache to clear, and no server write to make.
 */
export function shouldExpireCachedPicture(picture: AvatarPicture): boolean {
    return !!picture.src && !picture.fromFallback;
}
